import { useQuery } from "@tanstack/react-query";
import { CalendarCheck, Ticket, TrendingUp } from "lucide-react";
import type { JSX } from "react";

import { apiRequest } from "../../common/api/apiClient.js";
import {
  BookingStatusPill,
  type BookingStatus,
} from "../../common/components/BookingStatusPill.js";
import { Money } from "../../common/components/Money.js";
import {
  ServiceTypeBadge,
  serviceTypeClass,
} from "../../common/components/ServiceTypeBadge.js";
import type { components } from "../../generated/api.js";

type BookingDTO = components["schemas"]["BookingDTO"];

export function DashboardPage(): JSX.Element {
  const bookings = useQuery({
    queryKey: ["tenant-bookings"],
    queryFn: () => apiRequest<BookingDTO[]>("/bookings"),
  });

  const list = bookings.data ?? [];
  const confirmed = list.filter((entry) => entry.status === "CONFIRMED").length;
  const used = list.filter((entry) => entry.status === "USED").length;
  const revenue = list
    .filter((entry) => entry.status === "CONFIRMED" || entry.status === "USED")
    .reduce((sum, entry) => sum + entry.amountCents, 0);

  return (
    <div className="px-5 pb-8">
      <h1 className="pt-5 text-[22px] font-extrabold tracking-[-0.03em] text-ink">Übersicht</h1>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <div className="kpi">
          <span className="micro flex items-center gap-1.5">
            <Ticket size={13} aria-hidden="true" /> Gültige Tickets
          </span>
          <b>{confirmed}</b>
        </div>
        <div className="kpi">
          <span className="micro flex items-center gap-1.5">
            <CalendarCheck size={13} aria-hidden="true" /> Eingelöst
          </span>
          <b>{used}</b>
        </div>
        <div className="kpi">
          <span className="micro flex items-center gap-1.5">
            <TrendingUp size={13} aria-hidden="true" /> Umsatz
          </span>
          <b>
            <Money cents={revenue} />
          </b>
        </div>
      </div>

      <h2 className="sec">Letzte Buchungen</h2>

      {bookings.isPending && <p className="text-[14px] text-ink-3">Wird geladen …</p>}

      {bookings.isError && (
        <p className="text-[14px] text-ink-2">
          Für diese Rolle sind keine Buchungen sichtbar, oder der Mandant ist nicht gewählt.
        </p>
      )}

      {list.length === 0 && !bookings.isPending && !bookings.isError && (
        <p className="text-[14px] text-ink-3">Noch keine Buchung.</p>
      )}

      {list.length > 0 && (
        <div className="card tblscroll">
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">Angebot</th>
                <th scope="col">Art</th>
                <th scope="col">Status</th>
                <th scope="col" className="text-right">
                  Anzahl
                </th>
                <th scope="col" className="text-right">
                  Betrag
                </th>
              </tr>
            </thead>
            <tbody>
              {list.slice(0, 25).map((booking) => (
                <tr key={booking.id} className={serviceTypeClass(booking.serviceType)}>
                  <td className="font-semibold text-ink">{booking.serviceTitle}</td>
                  <td>
                    <ServiceTypeBadge type={booking.serviceType} />
                  </td>
                  <td>
                    <BookingStatusPill status={booking.status as BookingStatus} />
                  </td>
                  <td className="num text-right">{booking.quantity}</td>
                  <td className="num text-right">
                    <Money cents={booking.amountCents} currency={booking.currency} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
